import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components/macro";

import client from "../../api/client/SdsWebApiClient";

import { Card, CardTitle, CardDescription, CardButton, SecondaryCardButton } from "./Cards";

import GaReporter, { GaEcommerceAction } from "../../utilities/gaReporter";
import { SET_ERROR } from "../../store/actions/errorActions";
import { getSiteUrl } from "../../utilities/navigationUtils";
import {
    getCurrentSummaries,
    getFrontendSettings,
    getGuiTranslations,
    getSessionId,
    getShowPrice,
    getTotalPrice,
    getTouchScreen,
} from "../../store/sagas/selectors";
import InputFloatLabel from "../Form/InputFloatLabel";
import { CardData } from "../../models/summaryConfig";
import MailIcon from "../../icons/MailIcon.svg";

const mailformat = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

const Container = styled.div<ContainerProps>`
    visibility: ${props => (props.visible ? "visible" : "hidden")};
`;

const EmailInputContainer = styled.div`
    display: flex;
    flex-flow: column;
    align-items: center;
    width: 100%;
    color: ${props => props.theme.textColorSecondary};
`;

const InputError = styled.span`
    font-family: ${props => props.theme.fontFamily};
    font-size: 12px;
    align-self: flex-start;
    color: red;
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const FeedbackContainer = styled.div<ContainerProps>`
    visibility: ${props => (props.visible ? "visible" : "hidden")};
    ${({ theme }) => theme.bp.xs`
        width: calc(100% - 44px);
        padding: 20px;
        height: calc(100% - 40px);
    `};
    ${({ theme }) => theme.bp.sm`
        width: calc(100% - 24px);
        padding: 10px;
        height: calc(100% - 20px);
    `};
    ${({ theme }) => theme.bp.xxl`
        width: calc(100% - 64px);
    `};
    position: absolute;
    bottom: 0;
    padding: 10px;
    height: calc(100% - 20px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
`;

const FeedbackContent = styled.div``;

const SentIcon = styled.div`
    margin-top: 15px;
    svg {
        fill: currentColor;
        color: var(--primary-color);
`;

interface ContainerProps {
    visible: Boolean;
}

const SendSummaryCard = ({ config }: { config: CardData }) => {
    const dispatch = useDispatch();
    const sessionId = useSelector(getSessionId);
    const frontendSettings = useSelector(getFrontendSettings);
    const showPrice = useSelector(getShowPrice);
    const guiTranslations = useSelector(getGuiTranslations);
    const summaryGroups = useSelector(getCurrentSummaries);
    const isTouchScreen = useSelector(getTouchScreen);
    const totalPrice = useSelector(getTotalPrice);

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isDisabled, setIsDisabled] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    const sendCustomerEmail = async () => {
        try {
            if (!email || !email.match(mailformat)) {
                setEmailError(guiTranslations.get("PDF_TOOLTIP_INVALIDEMAIL"));
                return;
            }

            setEmailError("");

            if (!sessionId || !frontendSettings) return;

            setIsDisabled(true);

            const items = summaryGroups.flatMap(g => g.items);
            const siteUrl = getSiteUrl();
            await client.sendCustomerMailPost(sessionId, email, undefined, !showPrice, siteUrl);

            setEmailSent(true);

            GaReporter.event("cta_summary_email", "CTA");
            GaReporter.trackProductCheckout(
                GaEcommerceAction.Purchase,
                "SUMMARY_EMAIL",
                items,
                showPrice ? totalPrice : null
            );
        } catch (e) {
            dispatch({
                type: SET_ERROR,
                error: {
                    message: guiTranslations.get("PDF_TOOLTIP_EMAILFAILURE"),
                },
            });
        }
        setTimeout(() => {
            setIsDisabled(false);
        }, 2000);
    };

    return (
        <Card>
            <Container visible={!emailSent}>
                <CardTitle>{config.title}</CardTitle>
                <CardDescription>{config.description}</CardDescription>
                <EmailInputContainer>
                    <InputFloatLabel
                        name={guiTranslations.get("PDF_TEXT_EMAIL")}
                        error={emailError !== ""}
                        setEvent={setEmail}
                        autoComplete={isTouchScreen ? "new-password" : "email"}
                    />
                    {emailError !== "" ? <InputError>{emailError}</InputError> : null}
                </EmailInputContainer>
                <CardButton
                    isDisabled={isDisabled}
                    onClick={() => {
                        sendCustomerEmail();
                    }}
                >
                    <p>{config.buttonText}</p>
                </CardButton>
            </Container>

            <FeedbackContainer visible={emailSent}>
                <CardTitle>{config.title}</CardTitle>
                <FeedbackContent>
                    <SentIcon>
                        <MailIcon width={40} />
                    </SentIcon>
                </FeedbackContent>
                <CardDescription>
                    {config.emailSentDescription} {email}
                </CardDescription>
                <SecondaryCardButton
                    onClick={() => {
                        setEmailSent(false);
                        setIsDisabled(false);
                    }}
                >
                    <p>{config.feedbackButtonText}</p>
                </SecondaryCardButton>
            </FeedbackContainer>
        </Card>
    );
};

export default SendSummaryCard;
